import List from "components/list";
import { useParams } from "react-router-dom";
import { usePermissions } from "contexts/permission";
import { Button } from "ui/index";
import { Icon } from "components/index";
import { EDIT } from "icons/src";
import EmployeeInput from "components/employee/input";
import { useUpdateProject } from "@/modules/work/projects/project/crud/update/mutation";
import { eventBus } from "utils/eventBus";
import { UserFieldInput } from "permissions/components/user-field-input";
import ProjectNameCell from "@/modules/work/projects/cells/name";
import { ROUTE } from "@/lib/utils";

export default function AccountManagerList() {
  const { employeeId } = useParams();
  const userFilter = employeeId ? { _eq: employeeId } : { user: { _eq: "$CURRENT_USER" } };
  const permissions = usePermissions();
  const canReadClient = permissions.hasCollectionPermission("clients", "read");
  const canEditManager = permissions.hasFieldAccess("projects", "update", "account_manager");

  const storageKey = employeeId ? `profile-am-projects-${employeeId}` : "profile-am-projects";

  const updateProjectMutation = useUpdateProject();

  const handleUpdateSuccess = () => {
    eventBus.emit("refresh:list", `projects${storageKey}`);
  };

  const baseFields = [
    "id",
    "name",
    "project_id",
    "logo",
    "account_manager.id",
    "account_manager.first_name",
    "account_manager.last_name",
    "account_manager.avatar",
  ];

  const fields = [
    ...baseFields,
    ...(canReadClient
      ? ["client.first_name", "client.last_name", "client.company_id.name"]
      : []),
  ];

  const columns = [
    {
      id: "name",
      header: "Project",
      accessorKey: "name",
      cell: ProjectNameCell
    },
    {
      id: "client",
      header: "Client",
      accessorKey: "client",
      collection: "clients",
      cell: ({ getValue }) => {
        const c = getValue();
        if (!c) return null;
        const company = c.company_id?.name;
        const name = [c.first_name, c.last_name].filter(Boolean).join(" ");
        return <span>{company || name}</span>;
      },
    },
    {
      id: "account_manager",
      header: "AM",
      hidden: !canEditManager,
      href: false,
      cell: ({ row }) => {
        const project = row.original;
        return (
          <UserFieldInput
            targetItem={project.id}
            field="account_manager"
            collection="projects"
            showWhenDisabled={false}
          >
            <EmployeeInput
              set="account_manager"
              value={project.account_manager}
              singleSelect={true}
              footer={({ setOpen, selectedEmployees }) => (
                <Button
                  onClick={async () => {
                    const selectedEmployee = selectedEmployees?.[0];
                    if (selectedEmployee) {
                      await updateProjectMutation.mutateAsync({
                        projectId: project.id,
                        values: {
                          account_manager: selectedEmployee,
                        },
                      });
                      handleUpdateSuccess();
                    }
                    setOpen(false);
                  }}
                >
                  Assign
                </Button>
              )}
            >
              <Button variant="ghost">
                <Icon icon={EDIT} />
              </Button>
            </EmployeeInput>
          </UserFieldInput>
        );
      }
    }
  ];

  const filters = {
    account_manager: userFilter,
    status: { _eq: "active" },
  };

  return (
    <List
      title="Projects"
      storageKey={storageKey}
      endPoint="projects"
      filters={filters}
      columns={columns.filter(
        (col) => !col.hidden && (!col.collection || permissions.hasCollectionPermission(col.collection, "read"))
      )}
      meta={{ fields }}
      href={(item) => ROUTE.PROJECT(item.project_id)}
    />
  );
}
